<script setup lang="ts">
import { AvatarWithFallback } from '@/components/ui/avatar'
import type { FeedCardModel } from '@/components/feed/feed-card.model';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'vue-sonner';

const props = withDefaults(defineProps<{
  card: FeedCardModel;
  desktop?: boolean;
  drawer?: boolean;
  favoriteActive?: boolean;
  primaryLabel?: string | null;
  /** The CTA stays visible but inactive, e.g. «Вы откликнулись». */
  primaryDisabled?: boolean;
}>(), {
  desktop: false,
  drawer: false,
  favoriteActive: false,
  primaryLabel: null,
  primaryDisabled: false,
});

const emit = defineEmits<{
  action: [kind: 'favorite' | 'more' | 'primary'];
}>();

const revealedContacts = ref<Record<string, string> | null>(null);
const contactsLoading = ref(false);
const contacts = computed(() => revealedContacts.value ?? {});
const contactPhone = computed(() => {
  const value = contacts.value.phone;

  return value && value.trim().length > 0 ? value : null;
});
const contactEmail = computed(() => contacts.value.email?.trim() || null);
const formattedContactPhone = computed(() => {
  if (!contactPhone.value) return null;
  const digits = contactPhone.value.replace(/\D/g, '');
  if (!/^[78]\d{10}$/.test(digits)) return contactPhone.value;
  return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
});

watch(() => props.card.id, () => {
  revealedContacts.value = null;
});

const socialLinks = computed(() => [
  contacts.value.whatsapp
    ? { key: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/${contacts.value.whatsapp}`, icon: 'local:whatsapp' }
    : null,
  contacts.value.telegram
    ? { key: 'telegram', label: 'Telegram', href: `https://t.me/+${contacts.value.telegram}`, icon: 'local:telegram-blue' }
    : null,
].filter((link): link is NonNullable<typeof link> => link !== null));

async function revealContacts() {
  if (contactsLoading.value || revealedContacts.value !== null || !props.card.hasContacts) return;

  contactsLoading.value = true;
  try {
    const resource = props.card.target.kind === 'vacancy' ? 'vacancies' : 'summaries';
    const response = await mainFetch(`${resource}/${props.card.target.id}/contacts`);
    const payload = response.data.value;
    if (response.error.value || !payload?.contacts) {
      const status = response.error.value?.statusCode;
      if (status === 429) {
        toast.error('Лимит показа контактов исчерпан. Попробуйте позже.');
      } else {
        toast.error(apiErrorMessage(response.error.value, 'Не удалось показать контакты'));
      }
      return;
    }

    revealedContacts.value = payload.contacts as Record<string, string>;
  } finally {
    contactsLoading.value = false;
  }
}

</script>

<template>
  <Card
    :tabindex="props.desktop || props.drawer ? 0 : undefined"
    :class="[
      'app-feed-details gap-0 border-0 py-0 shadow-none ring-0',
      props.drawer && 'app-feed-details--drawer min-h-full',
      props.desktop && 'app-feed-details--desktop',
    ]"
  >
    <slot name="context" />
    <CardHeader class="app-feed-details__header gap-0 px-6 pb-6 pt-6">
      <AvatarWithFallback :src="props.card.avatar" :name="props.card.owner" class="size-12 border border-border" />
      <div class="mt-4 min-w-0">
        <p class="text-base leading-6 text-foreground">
          {{ props.card.owner }}
        </p>
        <CardTitle class="mt-1 text-2xl font-semibold leading-8">
          {{ props.card.title }}
        </CardTitle>
        <p
          v-if="props.card.supportingText"
          class="mt-1 text-sm leading-5 text-foreground"
        >
          {{ props.card.supportingText }}
        </p>
      </div>
    </CardHeader>

    <Separator class="mx-6 w-auto" />

    <CardContent class="app-feed-details__content flex flex-col gap-5 px-6 py-6">
      <div class="flex items-center justify-between gap-4">
        <dl class="flex min-w-0 flex-col gap-1">
          <dt class="text-sm leading-5 text-muted-foreground">
            Контакты
          </dt>
          <dd class="m-0 text-lg font-medium leading-6 text-foreground">
            <a v-if="contactPhone" :href="`tel:${contactPhone}`" class="no-underline underline-offset-2 hover:underline">{{ formattedContactPhone }}</a>
            <a v-else-if="contactEmail" :href="`mailto:${contactEmail}`" class="no-underline underline-offset-2 hover:underline">{{ contactEmail }}</a>
            <Button v-else-if="props.card.hasContacts && revealedContacts === null" variant="link" class="h-auto p-0 text-base font-medium leading-6" :disabled="contactsLoading" @click="revealContacts">
              {{ contactsLoading ? 'Загружаем контакты…' : 'Показать контакты' }}
            </Button>
            <span v-else-if="revealedContacts !== null">Не указаны</span>
            <span v-else>Не указаны</span>
          </dd>
        </dl>

        <div class="flex shrink-0 items-center gap-3">
          <Button
            v-for="social in socialLinks"
            :key="social.key"
            as-child
            variant="ghost"
            size="icon"
            class="size-9 rounded-full p-0 hover:bg-transparent"
          >
            <a
              :href="social.href"
              :aria-label="social.label"
              target="_blank"
              rel="noreferrer"
            >
              <Icon
                :name="social.icon"
                aria-hidden="true"
                class="size-9"
              />
            </a>
          </Button>
        </div>
      </div>

      <dl v-if="contactEmail && contactPhone" class="flex flex-col gap-1">
        <dt class="text-sm leading-5 text-muted-foreground">Почта</dt>
        <dd class="m-0 text-lg font-medium leading-6 text-foreground">
          <a :href="`mailto:${contactEmail}`" class="no-underline underline-offset-2 hover:underline">{{ contactEmail }}</a>
        </dd>
      </dl>

      <dl class="flex flex-col gap-5">
        <div
          v-for="row in props.card.details"
          :key="row.label"
          class="flex flex-col gap-1"
        >
          <dt class="text-sm leading-5 text-muted-foreground">
            {{ row.label }}
          </dt>
          <dd class="m-0 text-lg font-medium leading-6 text-foreground">
            {{ row.value }}
          </dd>
        </div>
      </dl>

      <template v-if="props.card.description">
        <Separator />
        <section class="flex flex-col gap-2">
          <h3 class="text-base font-semibold leading-6 text-foreground">
            {{ card.type === 'vacancy' ? 'О вакансии' : 'О себе' }}
          </h3>
          <p class="text-sm leading-5 text-muted-foreground">
            {{ props.card.description }}
          </p>
        </section>
      </template>
    </CardContent>

    <!-- Listing pages add blocks below the details (other vacancies, dates). -->
    <slot name="after" />

    <CardFooter
      v-if="props.drawer"
      class="app-feed-details__footer gap-3 px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3"
    >
      <Button
        v-if="props.primaryLabel !== null"
        type="button"
        class="h-12 flex-1 rounded-full text-base font-normal"
        :disabled="props.primaryDisabled"
        @click="emit('action', 'primary')"
      >
        {{ props.primaryLabel }}
      </Button>
      <Button
        type="button"
        variant="ghost"
        class="size-12 shrink-0 px-1 hover:bg-transparent active:bg-transparent"
        :class="props.favoriteActive ? 'text-destructive hover:text-destructive' : 'text-muted-foreground hover:text-muted-foreground'"
        :aria-label="props.favoriteActive ? 'Убрать из избранного' : 'Добавить в избранное'"
        :aria-pressed="props.favoriteActive"
        @click="emit('action', 'favorite')"
      >
        <Icon
          name="tabler:heart-filled"
          class="size-7"
          aria-hidden="true"
          :class="{ 'fill-current': props.favoriteActive }"
        />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        class="size-12 shrink-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
        aria-label="Дополнительные действия"
        @click="emit('action', 'more')"
      >
        <Icon
          name="tabler:dots-filled"
          class="size-7"
          aria-hidden="true"
        />
      </Button>
    </CardFooter>
  </Card>
</template>

<style scoped lang="scss">
.app-feed-details--desktop {
  position: relative;
  z-index: 5;
  width: var(--app-feed-desktop-card-width);
  height: var(--app-feed-desktop-card-height, min(68rem, calc(100dvh - 4rem)));
  overflow-y: auto;
  overscroll-behavior: contain;
}

.app-feed-details--drawer {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 0;
}

.app-feed-details--drawer .app-feed-details__footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  background: var(--card);
}

@media (max-width: 1100px) {
  .app-feed-details--desktop .app-feed-details__header,
  .app-feed-details--desktop .app-feed-details__content {
    padding-right: 1.25rem;
    padding-left: 1.25rem;
  }
}
</style>
